import React from "react";
import { formatDayMonthWithWeekday } from "../../utils/formatDate";
import {
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
} from "@mui/material";
import WeeklyComparisonTable from "./WeeklyComparisonTable";

interface DailyForecast {
  nights: number | string;
  people: number | string;
  occupancyRate: number | string;
}

interface ComparisonData {
  availableNights: { current: number | string; previous: number | string };
  occupiedNights: { current: number | string; previous: number | string };
  persons: { current: number | string; previous: number | string };
  averageOccupancy: { current: number | string; previous: number | string };
}

interface Props {
  startDate: Date;
  data: DailyForecast[]; // tableau de 14 jours (2 semaines)
  currentYear: number;
  comparisonWeek1?: ComparisonData;
  comparisonWeek2?: ComparisonData;
}

const WeeklyForecastTable = ({ startDate, data, currentYear, comparisonWeek1, comparisonWeek2 }: Props) => {
  const formatDate = (index: number) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + index);
    return formatDayMonthWithWeekday(date, "short");
  };

  // Séparer en 2 semaines
  const week1Data = data.slice(0, 7);
  const week2Data = data.slice(7, 14);

  const getTotal = (weekData: DailyForecast[], field: keyof DailyForecast) => {
    const validValues = weekData.filter(d => d[field] !== "N/A").map(d => typeof d[field] === "number" ? d[field] as number : 0);
    if (validValues.length === 0) return "N/A";
    return validValues.reduce((acc, val) => acc + val, 0);
  };

  const renderWeek = (weekData: DailyForecast[], weekLabel: string, startIndex: number) => (
    <Paper
      elevation={4}
      sx={{ mt: 4, boxShadow: 3, borderRadius: 2, overflow: "hidden" }}
    >
      <Typography
        variant="h6"
        align="center"
        sx={{ pt: 2, pb: 2, backgroundColor: "#fbeaec", fontWeight: 500 }}
      >
        {weekLabel}
      </Typography>
      <Table sx={{ maxWidth: "100%", boxShadow: 3, borderRadius: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell
              align="center"
              sx={{ fontSize: "1rem", backgroundColor: "#f5f5f5" }}
            >
            </TableCell>
            {weekData.map((_, index) => (
              <TableCell
                key={index}
                align="center"
                sx={{ fontSize: "1rem", backgroundColor: "#f5f5f5" }}
              >
                {formatDate(startIndex + index)}
              </TableCell>
            ))}
            <TableCell
              align="center"
              sx={{
                fontSize: "1rem",
                backgroundColor: "#f5f5f5",
                color: "#bf2f38",
              }}
            >
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[
            { label: "Nuitées", field: "nights" },
            { label: "Personnes", field: "people" },
            { label: "Taux d'occupation", field: "occupancyRate", suffix: "%" },
          ].map((row, idx) => (
            <TableRow
              key={idx}
              sx={{ backgroundColor: idx % 2 === 0 ? "#fafafa" : "#fff" }}
            >
              <TableCell
                align="center"
                sx={{ fontSize: "0.9rem", color: "#222222" }}
              >
                {row.label}
              </TableCell>
              {weekData.map((d, i) => (
                <TableCell
                  key={i}
                  align="center"
                  sx={{ fontSize: "0.9rem", color: "#222222" }}
                >
                  {d[row.field as keyof DailyForecast]}
                  {row.suffix && d[row.field as keyof DailyForecast] !== "N/A" ? row.suffix : ""}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{ fontSize: "0.9rem", color: "#bf2f38" }}
              >
                {row.label === "Taux d'occupation"
                  ? (() => {
                      const total = getTotal(weekData, row.field as keyof DailyForecast);
                      return total === "N/A" ? "N/A" : `${((total as number) / 7).toFixed(2)}%`;
                    })()
                  : getTotal(weekData, row.field as keyof DailyForecast)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );

  return (
    <Box sx={{ mb: 6 }}>
      {/* Semaine 1 */}
      {renderWeek(week1Data, "Détails hebdomadaires prévisionnelles - Semaine 1", 0)}
      {comparisonWeek1 && (
        <WeeklyComparisonTable 
          currentYear={currentYear} 
          title="Comparaison hebdomadaire - Semaine 1"
          data={comparisonWeek1} 
        />
      )}
      
      {/* Espacement entre les deux semaines */}
      <Box sx={{ mt: 8 }} />

      {/* Semaine 2 */}
      {renderWeek(week2Data, "Détails hebdomadaires prévisionnelles - Semaine 2", 7)}
      {comparisonWeek2 && (
        <WeeklyComparisonTable 
          currentYear={currentYear} 
          title="Comparaison hebdomadaire - Semaine 2"
          data={comparisonWeek2} 
        />
      )}
    </Box>
  );
};

export default WeeklyForecastTable;
